import { getCore } from '../../../client/core/getCore';
import { getDefaultClient } from '../../../client/defaultClient';
import type { WalletAccount } from '../walletAccount';
import { getWalletAccountsFromState } from './getWalletAccountsFromState';

/**
 * Retrieves all wallet accounts associated with the current session.
 *
 * This function returns both verified and unverified wallet accounts,
 * combining data from user credentials and local unverified accounts.
 * You can differentiate between verified and unverified wallet accounts by
 * checking the `verifiedCredentialId` property.
 *
 * @example
 * ```ts
 * const wallets = getWalletAccounts();
 * const verified = wallets.filter((walletAccount) =>
 *   isWalletAccountVerified({ walletAccount }),
 * );
 * ```
 *
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns An array of wallet accounts associated with the session.
 * @see isWalletAccountVerified
 * @see verifyWalletAccount
 * @notInstrumented
 * @hook Exposed as a hook so consumers get the reactive list of wallet accounts.
 */
export const getWalletAccounts = (
  client = getDefaultClient()
): WalletAccount[] => {
  const core = getCore(client);
  const { unverifiedWalletAccounts, user } = core.state.get();

  return getWalletAccountsFromState({ unverifiedWalletAccounts, user }, client);
};
